import CONFIG from 'isomorphic-config'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { LOCATION_SEARCH_TYPE } from '@/types'

import { top20Filters } from './constants'
import { fireFilterBarGA4Event } from './FilterBar/ga4'
import type { TypeAheadConfigEndpoint } from './types'
import { cityIdExistsInTop20Filters } from './utils'

type UseTypeAheadOptions = {
  searchValue: string | null
  searchKey: string
  showHotelTypeaheadFilters?: boolean
  onLoaded?: () => void
  typeAheadEndpoint: TypeAheadConfigEndpoint
  debounceTime?: number
  airportNestingEnabled?: boolean
  hasAISearch?: boolean
  hasLTRSearch?: boolean
}

type TypeAheadState = {
  loading: boolean
  error: unknown
  items: LOCATION_SEARCH_TYPE[]
  query: string
  city?: string
  duration?: number
  source?: string
}

function isAbortError(error: unknown) {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
}

function useTypeAhead({
  searchValue,
  searchKey,
  showHotelTypeaheadFilters = false,
  onLoaded = () => null,
  typeAheadEndpoint,
  debounceTime = 100,
  airportNestingEnabled = false,
  hasAISearch = false,
  hasLTRSearch = false,
}: UseTypeAheadOptions) {
  const [state, setState] = useState<TypeAheadState>({
    loading: false,
    error: null,
    items: [],
    query: '',
  })

  const prevSearchValueRef = useRef<string | null>(null)
  const onLoadedRef = useRef(onLoaded)
  useEffect(() => {
    onLoadedRef.current = onLoaded
  }, [onLoaded])

  const mountedRef = useRef(false)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const abortControllerRef = useRef<AbortController | null>(null)

  const abortCurrentRequest = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
  }, [])

  const fetchTypeaheadData = useCallback(
    (query: string) => {
      abortCurrentRequest()
      const controller = new AbortController()
      abortControllerRef.current = controller

      const locationConfig = CONFIG.client.typeahead.location
      const isRCSearchEndPoint = typeAheadEndpoint === 'cars'
      const parsedURLSuffix = isRCSearchEndPoint ? '' : locationConfig.suffix[typeAheadEndpoint]
      const parseURLSuffixQS = isRCSearchEndPoint ? locationConfig.querystring.cars : ''
      let rawUrl = `${locationConfig.endpoint[typeAheadEndpoint]}${query}${parsedURLSuffix}${parseURLSuffixQS}`

      if (hasAISearch) {
        const aiSep = rawUrl.includes('?') ? '&' : '?'
        rawUrl += `${aiSep}searchModes=ai-search`
      }

      if (hasLTRSearch) {
        const ltrSep = rawUrl.includes('?') ? '&' : '?'
        rawUrl += `${ltrSep}rankBy=RELEVANCE`
      }

      const querySeparator = rawUrl.includes('?') ? '&' : '?'
      const url = airportNestingEnabled ? `${rawUrl}${querySeparator}airportNestingEnabled=true` : rawUrl

      setState((prev) => ({ ...prev, loading: true, error: null }))

      fetch(url, { signal: controller.signal })
        .then((res) => res.json())
        .then(
          ({
            searchItems,
            duration,
            source,
          }: {
            searchItems: LOCATION_SEARCH_TYPE[] | null
            duration: number
            source: string
          }) => {
            if (!mountedRef.current || abortControllerRef.current !== controller || controller.signal.aborted)
              return

            const safeItems = searchItems ?? []

            setState((prev) => ({
              ...prev,
              loading: false,
              error: null,
              items: safeItems,
              query,
              duration,
              source,
            }))
            onLoadedRef.current()

            const cityFilters =
              showHotelTypeaheadFilters &&
              safeItems.length > 0 &&
              cityIdExistsInTop20Filters(safeItems[0].cityID)
                ? top20Filters[safeItems[0].cityID]
                : []

            if (showHotelTypeaheadFilters && cityFilters.length > 0) {
              fireFilterBarGA4Event()
            }
          },
        )
        .catch((error) => {
          if (isAbortError(error)) return
          if (!mountedRef.current || abortControllerRef.current !== controller || controller.signal.aborted)
            return
          setState((prev) => ({ ...prev, loading: false, error, query }))
          onLoadedRef.current()
        })
    },
    [
      abortCurrentRequest,
      typeAheadEndpoint,
      showHotelTypeaheadFilters,
      airportNestingEnabled,
      hasAISearch,
      hasLTRSearch,
    ],
  )
  const debouncedFetchRef = useRef<ReturnType<typeof debounce> | null>(null)

  // Initialize or update the debounced fetch function when dependencies change
  useEffect(() => {
    if (debouncedFetchRef.current) {
      debouncedFetchRef.current.cancel()
    }

    debouncedFetchRef.current = debounce((query: string) => {
      fetchTypeaheadData(query)
    }, debounceTime)

    return () => {
      if (debouncedFetchRef.current) {
        debouncedFetchRef.current.cancel()
      }
      abortCurrentRequest()
    }
  }, [abortCurrentRequest, fetchTypeaheadData, debounceTime])

  useEffect(() => {
    if (prevSearchValueRef.current !== searchValue) {
      prevSearchValueRef.current = searchValue

      if (searchValue && searchValue.trim()) {
        abortCurrentRequest()
        if (debouncedFetchRef.current) {
          debouncedFetchRef.current(searchValue.trim())
        }
      } else {
        abortCurrentRequest()
      }
    }
  }, [abortCurrentRequest, searchValue, searchKey])

  const search = useCallback(
    (value: string) => {
      if (value.trim() && debouncedFetchRef.current) {
        abortCurrentRequest()
        debouncedFetchRef.current(value.trim())
      }
    },
    [abortCurrentRequest],
  )

  const isEmpty = !searchValue?.trim()
  const isStale = state.query !== searchValue?.trim()

  return {
    loading: isEmpty ? false : isStale ? true : state.loading,
    error: isEmpty || isStale ? null : state.error,
    items: isEmpty || isStale ? [] : state.items,
    city: isEmpty || isStale ? undefined : state.city,
    duration: isEmpty || isStale ? undefined : state.duration,
    source: isEmpty || isStale ? undefined : state.source,
    search,
  }
}

export default useTypeAhead
